import { useInternationalization } from '@progress/kendo-react-intl';
import { useMemo } from 'react';
import ModalContentPreference from 'src/components/ModalContentPreference';
import NumberInput from 'src/components/NumberInput';
import PopUp from 'src/components/design-system/PopUp';
import { getBgColor, getIcon } from 'src/utils/icons';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';
import OcrIndicator from '../split_line_item/OcrIndicator';

const Taxes = () => {
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const ocrResponse = useContextSelector(InvoiceContext, (value) => value.ocrResponse);
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const handleInputChange = useContextSelector(InvoiceContext, (value) => value.handleInputChange);
  const warnings = useContextSelector(InvoiceContext, (value) => value.warnings);
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const errors = useContextSelector(InvoiceContext, (value) => value.errors);
  const hasWarning = useMemo(() => !!warnings.salesTaxAmt.length, [warnings.salesTaxAmt.length]);
  const hasError = useMemo(() => !!errors.salesTaxAmt.length, [errors.salesTaxAmt.length]);
  const intl = useInternationalization();

  const isEdited = useMemo(
    () => Number(ocrResponse.totalTax?.value || 0) !== Number(invoice?.salesTaxAmt || 0),
    [invoice?.salesTaxAmt, ocrResponse.totalTax?.value]
  );
  const showOcrIndicator = useMemo(() => {
    const confidence = ocrResponse?.totalTax?.confidence;
    const hasMeaningfulConfidence =
      confidence != null && confidence !== '' && parseFloat(confidence) !== 0;
    return Boolean(
      initialInvoice?.ocrGenerated &&
        hasMeaningfulConfidence &&
        Number(invoice?.salesTaxAmt) !== 0
    );
  }, [initialInvoice?.ocrGenerated, ocrResponse?.totalTax?.confidence, invoice?.salesTaxAmt]);

  return (
    <PopUp
      content={
        <ModalContentPreference
          hasWarning={hasWarning}
          hasError={hasError}
          warningContent={warnings.salesTaxAmt}
          errorContent={errors.salesTaxAmt}
        />
      }
      show={hasError || hasWarning}
      popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
      anchorAlign={{ horizontal: 'right', vertical: 'top' }}
    >
      <div className={`p-1 ${getBgColor(errors.salesTaxAmt, warnings.salesTaxAmt)} text-[13px]`}>
        <div className="flex justify-between max-w-[177px] flex-grow">
          <p className="font-bold  flex items-center pb-1">
            Taxes
            <span className="px-2">{getIcon(errors.salesTaxAmt, warnings.salesTaxAmt)}</span>
          </p>
          {showOcrIndicator && (
            <OcrIndicator
              isEdited={isEdited}
              ocrConfidence={ocrResponse.totalTax?.confidence ?? ''}
              onHover={() => handleHoverOcrField('totalTax')}
            />
          )}
        </div>
        {isInvoiceEditDisabled ? (
          <p title={invoice?.salesTaxAmt?.toString() || ''}>
            {intl.format('{0:c2}', Number(invoice?.salesTaxAmt) || 0)}
          </p>
        ) : (
          <NumberInput
            title={invoice?.salesTaxAmt?.toString() || ''}
            value={invoice?.salesTaxAmt.toString()}
            onChange={handleInputChange('salesTaxAmt')}
            onFocus={() => handleHoverOcrField('totalTax')}
            prefix="$"
            valid={!(hasWarning || hasError)}
            ocr-data="totalTax"
          />
        )}
      </div>
    </PopUp>
  );
};

export default Taxes;
